import { Box, Chip, Typography } from '@mui/material';

import { convertCriteriaGroupActivity } from '../../../../../../api/enumerations';
import {
  CriteriaAnalysisGroup,
  NoticeDocumentsData,
} from '../../../../../../api/theHive/notices/types';
import DocumentsTable from './DocumentsTable';

interface EngineeringCriteriaSectionsProps {
  groups: CriteriaAnalysisGroup[];
  engineeringDocs: NoticeDocumentsData[];
  docLoading: boolean;
  handleReplace: (docId: number) => Promise<void>;
  handleApprove: (docId?: number) => Promise<void>;
  handleReject: (reason: string, docId?: number) => Promise<void>;
  disabled: boolean;
}

export function EngineeringCriteriaSections({
  groups,
  engineeringDocs,
  docLoading,
  handleReplace,
  handleApprove,
  handleReject,
  disabled,
}: EngineeringCriteriaSectionsProps): JSX.Element {
  const groupedIds = new Set(
    groups.flatMap((group) =>
      group.documents
        .map((doc) => doc.notice_company_document_id)
        .filter((id): id is number => id != null)
    )
  );
  const others = engineeringDocs.filter((doc) => !groupedIds.has(doc.id));

  // Sem grupos de critério: cai na exibição simples (lista de engenharia).
  if (groups.length === 0) {
    return (
      <DocumentsTable
        name="Análise de Engenharia"
        docList={engineeringDocs}
        docLoading={docLoading}
        handleReplace={handleReplace}
        handleApprove={handleApprove}
        handleReject={handleReject}
        disabled={disabled}
      />
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" my={2}>
        <Typography fontWeight={600}>Credenciando para:</Typography>
        {groups.map((group) => (
          <Chip
            key={group.criteria_group_id}
            size="small"
            color="secondary"
            variant="outlined"
            label={group.group_name}
          />
        ))}
      </Box>

      {groups.map((group) => {
        const ids = new Set(
          group.documents
            .map((doc) => doc.notice_company_document_id)
            .filter((id): id is number => id != null)
        );
        const docsOfGroup = engineeringDocs.filter((doc) => ids.has(doc.id));
        const notSent = group.documents.filter(
          (doc) => doc.notice_company_document_id == null
        );

        return (
          <Box key={group.criteria_group_id} mt={3}>
            <Typography color="text.secondary" fontSize={14}>
              Critério ({group.group_name} ·{' '}
              {convertCriteriaGroupActivity(group.activity)}):{' '}
              {group.criteria_description || '—'}
            </Typography>
            <DocumentsTable
              name={group.group_name}
              docList={docsOfGroup}
              docLoading={docLoading}
              handleReplace={handleReplace}
              handleApprove={handleApprove}
              handleReject={handleReject}
              disabled={disabled}
            />
            {notSent.length > 0 && (
              <Typography color="text.secondary" fontSize={13} mt={0.5}>
                Faltando enviar: {notSent.map((doc) => doc.name).join(', ')}
              </Typography>
            )}
          </Box>
        );
      })}

      {others.length > 0 && (
        <Box mt={3}>
          <DocumentsTable
            name="Outros documentos de engenharia"
            docList={others}
            docLoading={docLoading}
            handleReplace={handleReplace}
            handleApprove={handleApprove}
            handleReject={handleReject}
            disabled={disabled}
          />
        </Box>
      )}
    </Box>
  );
}
